import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { TranslationRepository } from '../../domain/repositories/translation.repository';
import { RoleTranslation, RoleTranslationCollection } from '../../domain/entities/role-translation.entity';
import { TRANSLATION_CONFIG } from '../config/translation.config';

/**
 * Infrastructure Implementation
 * Implementación concreta del repositorio de traducciones
 */
@Injectable({
  providedIn: 'root'
})
export class TranslationRepositoryImpl implements TranslationRepository {
  
  private readonly ROLE_TRANSLATIONS_KEY = 'roles';
  
  constructor(private http: HttpClient) {}

  getRoleTranslations(language: string): Promise<RoleTranslationCollection> {
    const translationUrl = `${TRANSLATION_CONFIG.translationPath}/${language}.json`;
    
    return this.http.get<any>(translationUrl)
      .pipe(
        map(data => this.mapToRoleTranslations(data, language)),
        catchError(() => this.getDefaultRoleTranslations(language))
      )
      .toPromise() as Promise<RoleTranslationCollection>;
  }

  getRoleTranslation(roleKey: string, language: string): Promise<string> {
    return this.getRoleTranslations(language)
      .then(collection => {
        const translation = collection.translations.find(t => t.key === roleKey);
        return translation?.value || roleKey;
      });
  }

  private mapToRoleTranslations(data: any, language: string): RoleTranslationCollection {
    const roleTranslations = data[this.ROLE_TRANSLATIONS_KEY] || {};
    
    const translations: RoleTranslation[] = Object.entries(roleTranslations).map(([key, value]) => ({
      key,
      value: value as string,
      language
    }));

    return {
      translations,
      language
    };
  }

  private getDefaultRoleTranslations(language: string): Observable<RoleTranslationCollection> {
    const fallbackLanguage = TRANSLATION_CONFIG.fallbackLanguage;
    if (language !== fallbackLanguage) {
      return from(this.getRoleTranslations(fallbackLanguage));
    }
    // Si incluso el fallback falla, retorna las claves sin traducir
    const emptyTranslations: RoleTranslation[] = [
      { key: 'Employee', value: 'Employee', language },
      { key: 'AdminWorkCenter', value: 'AdminWorkCenter', language },
      { key: 'Root', value: 'Root', language },
      { key: 'AdminCompany', value: 'AdminCompany', language }
    ];

    return of({
      translations: emptyTranslations,
      language
    });
  }
}
